import React, { Suspense, lazy, useRef, useState, useLayoutEffect } from 'react';
import {
  Router,
  UNSAFE_createBrowserHistory as createBrowserHistory,
  type Location,
  type NavigationType,
} from 'react-router';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './i18n';
import './styles/rtl.css';
import Layout from './components/Layout';
import ScrollPrevention from './components/ScrollPrevention';
import LoadingSpinner from './components/LoadingSpinner';

const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      // Some mobile browsers keep stale chunk references after deploys.
      // Reload once to fetch the newest assets instead of showing a blank page.
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        const retryKey = 'lazy-retry-register';
        if (!sessionStorage.getItem(retryKey)) {
          sessionStorage.setItem(retryKey, '1');
          window.location.reload();
          return new Promise<never>(() => {});
        }
        sessionStorage.removeItem(retryKey);
      }
      throw error;
    }
  });

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Route rendering error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white shadow-lg rounded-xl p-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to load this page</h1>
            <p className="text-sm text-gray-600 mb-4">
              Please refresh. If the issue continues, clear browser cache and try again.
            </p>
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-[#0D9488] text-white hover:bg-[#0F766E]"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load all pages for code splitting and better performance
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Clients = lazy(() => import('./pages/Clients'));
const Employees = lazy(() => import('./pages/Employees'));
const Stores = lazy(() => import('./pages/Stores'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Payments = lazy(() => import('./pages/Payments'));
const Reports = lazy(() => import('./pages/Reports'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const TodoManagement = lazy(() => import('./pages/TodoManagement'));
const MyTodos = lazy(() => import('./pages/MyTodos'));
const AdminCalendar = lazy(() => import('./pages/AdminCalendar'));
const EmployeeCalendar = lazy(() => import('./pages/EmployeeCalendar'));
const ClientRegistration = lazyWithRetry(() => import('./pages/ClientRegistration'));
const DailyRecaps = lazy(() => import('./pages/DailyRecaps'));
const PacksManagement = lazy(() => import('./pages/PacksManagement'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const VerificationCenter = lazy(() => import('./pages/VerificationCenter'));
const CheckIns = lazy(() => import('./pages/CheckIns'));
const PaymentAlerts = lazy(() => import('./pages/PaymentAlerts'));
const RecurringExpenses = lazy(() => import('./pages/RecurringExpenses'));
const Chatbot = lazy(() => import('./pages/Chatbot'));
const Agent = lazy(() => import('./pages/Agent'));
const ApiReference = lazy(() => import('./pages/ApiReference'));
const RelanceExcel = lazy(() => import('./pages/RelanceExcel'));
const Digests = lazy(() => import('./pages/Digests'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner />
  </div>
);

// Protected Route Component (just checks authentication)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Page Permission Route Component (checks can_view permission)
const PagePermissionRoute: React.FC<{ children: React.ReactNode; pageName: string }> = ({ children, pageName }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin has access to everything
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  if (isAdmin) {
    return <>{children}</>;
  }

  // Special case: Employees can always access recaps page to create and view their own recaps
  if (pageName === 'recaps') {
    return <>{children}</>;
  }

  // Check can_view permission
  const permissions = user?.permissions;
  if (permissions?.pagePermissions) {
    const pagePerm = permissions.pagePermissions.find((p: any) => p.page === pageName);
    if (pagePerm && pagePerm.can_view === 1) {
      return <>{children}</>;
    }
  }

  // No permission - redirect to dashboard
  console.log(`[PagePermissionRoute] Access denied for page: ${pageName}`);
  return <Navigate to="/dashboard" replace />;
};

// Admin Only Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  
  if (!isAdmin) {
    // Redirect non-admin users to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

/**
 * React Router v7's BrowserRouter wraps location updates in startTransition.
 * On heavy pages (e.g. Agent), that can leave the URL updated while the previous
 * route still paints. This mirrors BrowserRouter with a synchronous setState.
 */
const SyncBrowserRouter: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const historyRef = useRef<ReturnType<typeof createBrowserHistory> | null>(null);
  if (historyRef.current == null) {
    historyRef.current = createBrowserHistory({ window, v5Compat: true });
  }
  const history = historyRef.current;
  const [state, setState] = useState<{
    action: NavigationType;
    location: Location;
  }>({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen((update: { action: NavigationType; location: Location }) => {
    setState({ action: update.action, location: update.location });
  }), [history]);

  return (
    <Router
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
};

// Single Layout Wrapper that persists across all route changes
const ProtectedLayoutWrapper: React.FC = () => {
  const location = useLocation();
  return (
    <Layout>
      <Outlet key={location.pathname} />
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <SyncBrowserRouter>
        <ScrollPrevention />
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Suspense fallback={<PageLoader />}>
                    <Login />
                  </Suspense>
                </PublicRoute>
              } 
            />
            
            {/* Public Client Registration - No auth required */}
            <Route 
              path="/register" 
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ClientRegistration />
                  </Suspense>
                </RouteErrorBoundary>
              } 
            />

            {/* Public API documentation (no login required; bookmarkable) */}
            <Route
              path="/docs"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ApiReference variant="public" />
                </Suspense>
              }
            />

            {/* All Protected Routes - Single persistent Layout wrapper */}
            <Route element={<ProtectedLayoutWrapper />}>
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Dashboard />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/clients" 
                element={
                  <PagePermissionRoute pageName="clients">
                    <Suspense fallback={<PageLoader />}>
                      <Clients />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/checkins" 
                element={
                  <PagePermissionRoute pageName="checkins">
                    <Suspense fallback={<PageLoader />}>
                      <CheckIns />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/employees" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Employees />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/stores" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Stores />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/subscriptions" 
                element={
                  <PagePermissionRoute pageName="subscriptions">
                    <Suspense fallback={<PageLoader />}>
                      <Subscriptions />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/invoices" 
                element={
                  <PagePermissionRoute pageName="invoices">
                    <Suspense fallback={<PageLoader />}>
                      <Invoices />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/payments" 
                element={
                  <PagePermissionRoute pageName="payments">
                    <Suspense fallback={<PageLoader />}>
                      <Payments />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/payment-alerts" 
                element={
                  <PagePermissionRoute pageName="payments">
                    <Suspense fallback={<PageLoader />}>
                      <PaymentAlerts />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/reports" 
                element={
                  <PagePermissionRoute pageName="reports">
                    <Suspense fallback={<PageLoader />}>
                      <Reports />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/recurring-expenses" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RecurringExpenses />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Profile />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Settings />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/todos" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <TodoManagement />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/my-todos" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <MyTodos />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin-calendar" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AdminCalendar />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/my-calendar" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <EmployeeCalendar />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/recaps" 
                element={
                  <PagePermissionRoute pageName="recaps">
                    <Suspense fallback={<PageLoader />}>
                      <DailyRecaps />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/packs" 
                element={
                  <PagePermissionRoute pageName="packs">
                    <Suspense fallback={<PageLoader />}>
                      <PacksManagement />
                    </Suspense>
                  </PagePermissionRoute>
                } 
              />
              <Route 
                path="/audit-logs" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <AuditLogs />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/verification-center" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <VerificationCenter />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/chatbot" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Chatbot />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/agent" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Agent />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route
                path="/api-docs"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <ApiReference variant="embedded" />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/relance-excel"
                element={
                  <AdminRoute>
                    <Suspense fallback={<PageLoader />}>
                      <RelanceExcel />
                    </Suspense>
                  </AdminRoute>
                }
              />
              <Route
                path="/digests"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Digests />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </SyncBrowserRouter>
    </AuthProvider>
  );
}

export default App;
